import { Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

export function Panel({ title, action, children, className = "", testid }) {
  return (
    <div className={`rounded-xl border border-slate-800 bg-[#111827] ${className}`} data-testid={testid}>
      {(title || action) && (
        <div className="flex items-center justify-between border-b border-slate-800 px-5 py-3.5">
          <h3 className="font-display text-sm font-bold uppercase tracking-wider text-slate-200">{title}</h3>
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight text-white">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-slate-400">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

export function KpiCard({ icon: Icon, label, value, sub, accent = "blue", testid, to }) {
  const navigate = useNavigate();
  const colors = {
    blue: "text-blue-400 bg-blue-500/10",
    amber: "text-amber-400 bg-amber-500/10",
    emerald: "text-emerald-400 bg-emerald-500/10",
    red: "text-red-400 bg-red-500/10",
    slate: "text-slate-300 bg-slate-500/10",
  };
  const clickable = !!to;
  const inner = (
    <>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</span>
        <span className={`flex h-9 w-9 items-center justify-center rounded-lg ${colors[accent]}`}><Icon className="h-4 w-4" /></span>
      </div>
      <div className="mt-3 font-mono-data text-3xl font-bold text-white">{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
    </>
  );
  if (clickable) {
    return (
      <button type="button" onClick={() => navigate(to)} data-testid={testid}
        className="group w-full rounded-xl border border-slate-800 bg-[#111827] p-5 text-left transition-colors hover:border-blue-600/60 hover:bg-[#141c2e] focus:outline-none focus:ring-2 focus:ring-blue-600">
        {inner}
      </button>
    );
  }
  return (
    <div className="rounded-xl border border-slate-800 bg-[#111827] p-5 transition-colors hover:border-slate-700" data-testid={testid}>
      {inner}
    </div>
  );
}

// Robust status coloring: normalize then match known tokens, fall back by keyword.
const EXACT = {
  on_track: "emerald", completed: "emerald", active: "emerald", valid: "emerald", passed: "emerald", pass: "emerald",
  resolved: "emerald", available: "emerald", picked_up: "blue", passenger_on_board: "blue", in_progress: "blue",
  en_route: "cyan", enroute: "cyan", driver_en_route: "cyan", arrived: "amber", driver_arrived: "amber",
  assigned: "purple", acknowledged: "teal", scheduled: "sky", unassigned: "sky", pending: "slate",
  under_review: "amber", open: "red", at_risk: "amber",
  predicted_late: "orange", expiring: "amber", maintenance: "amber", inactive: "slate",
  off_duty: "slate", missed: "red", late: "red", expired: "red", cancelled: "gray", canceled: "gray", no_show: "gray",
  out_of_service: "red", high: "red", medium: "amber", low: "slate",
};
const TONE = {
  emerald: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  blue: "bg-blue-600/25 text-blue-200 border-blue-500/50",
  sky: "bg-sky-500/10 text-sky-300 border-sky-500/30",
  cyan: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30",
  teal: "bg-teal-500/15 text-teal-300 border-teal-500/30",
  purple: "bg-purple-500/15 text-purple-300 border-purple-500/30",
  amber: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  orange: "bg-orange-500/15 text-orange-300 border-orange-500/40",
  red: "bg-red-500/15 text-red-300 border-red-500/30",
  gray: "bg-slate-700/30 text-slate-400 border-slate-600/40 line-through decoration-slate-500/60",
  slate: "bg-slate-500/15 text-slate-300 border-slate-500/30",
};
const DOT = { emerald: "bg-emerald-400", blue: "bg-blue-400", sky: "bg-sky-400", cyan: "bg-cyan-400", teal: "bg-teal-400",
  purple: "bg-purple-400", amber: "bg-amber-400", orange: "bg-orange-400", red: "bg-red-400", gray: "bg-slate-500", slate: "bg-slate-400" };

const TRIP_TONE = { completed: "emerald", cancelled: "red", no_show: "red", missed: "red", on_hold: "amber",
  unassigned: "blue", assigned: "blue", will_call: "blue", driver_en_route: "blue", arrived: "blue", passenger_on_board: "blue", in_progress: "blue" };

function toneFor(value) {
  const key = String(value || "").toLowerCase().trim().replace(/\s+/g, "_");
  if (TRIP_TONE[key]) return TRIP_TONE[key];
  if (EXACT[key]) return EXACT[key];
  if (/(cancel|no.?show)/.test(key)) return "gray";
  if (/(miss|expired|out_of|fail)/.test(key)) return "red";
  if (/predicted/.test(key)) return "orange";
  if (/(risk|late|expir|review|warn|pending_|arriv)/.test(key)) return "amber";
  if (/(complete|valid|resolve|on_track|active|done|ok)/.test(key)) return "emerald";
  if (/route/.test(key)) return "cyan";
  if (/(board|progress|transit|pickup)/.test(key)) return "blue";
  if (/assign/.test(key)) return "purple";
  return "slate";
}

export function StatusPill({ value }) {
  const tone = toneFor(value);
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-0.5 font-mono-data text-[11px] font-semibold uppercase tracking-wide ${TONE[tone]}`}>
      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${DOT[tone]}`} aria-hidden="true" />
      {String(value || "—").replace(/_/g, " ")}
    </span>
  );
}

// Risk with context: predicted (forecast) vs confirmed lateness, and missing ETA.
export function RiskBadge({ trip, testid }) {
  const r = trip?.risk;
  const delay = Number(trip?.predicted_delay_min) || 0;
  let label = String(r || "—").replace(/_/g, " ");
  if (r === "predicted_late") label = delay ? `Predicted late +${delay} min` : "Predicted late · no ETA";
  else if (r === "missed") label = "Late · confirmed";
  else if (r === "at_risk" && delay) label = `At risk +${delay} min`;
  const tone = r === "predicted_late" ? "orange" : r === "missed" ? "red" : toneFor(r);
  return (
    <span data-testid={testid} title={r === "predicted_late" ? "Forecast from the driver's ETA — not late yet" : undefined}
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-0.5 font-mono-data text-[11px] font-semibold uppercase tracking-wide ${TONE[tone]}`}>
      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${DOT[tone]}`} aria-hidden="true" />{label}
    </span>
  );
}

export const RISK_ORDER = { missed: 0, predicted_late: 1, at_risk: 2, on_track: 3 };

export function Loading({ label = "Loading live operations data…" }) {
  return (
    <div className="flex h-64 flex-col items-center justify-center gap-3 text-slate-500" data-testid="portal-data-loading">
      <Loader2 className="h-7 w-7 animate-spin text-blue-500" />
      <span className="text-sm">{label}</span>
    </div>
  );
}

export function EmptyState({ label }) {
  return <div className="px-5 py-10 text-center text-sm text-slate-500">{label}</div>;
}
